import { Star, MapPin, Tag, Sparkles, Phone, Globe, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Business } from "./StepBusinessSearch";

interface StepBusinessConfirmProps {
  business: Business;
  onBack: () => void;
  onConfirm: () => void;
}

export const StepBusinessConfirm = ({ business, onBack, onConfirm }: StepBusinessConfirmProps) => {
  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Confirm Your Business</h1>
        <p className="text-muted-foreground">
          Make sure this is the right business profile
        </p>
      </div>

      <div className="max-w-2xl mx-auto bg-card rounded-2xl border border-border p-8 shadow-lg">
        <div className="space-y-6">
          {/* Business Image */}
          <div className="relative w-full h-48 rounded-xl overflow-hidden bg-muted">
            <img
              src={business.thumbnail}
              alt={business.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder.svg";
              }}
            />
          </div>

          {/* Business Name & Status */}
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <h2 className="text-2xl font-bold">{business.name}</h2>
              <Badge 
                variant={business.isOpen ? "default" : "secondary"}
                className="text-sm"
              >
                {business.isOpen ? "Open Now" : "Closed"}
              </Badge>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < Math.floor(business.rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted"
                    }`}
                  />
                ))}
              </div>
              <span className="font-semibold">{business.rating}</span>
              <span className="text-muted-foreground">({business.reviewCount} reviews)</span>
            </div>

            {/* Address */}
            <div className="flex items-start gap-2 text-muted-foreground">
              <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>{business.address}</span>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col gap-2 pt-2">
                {business.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>{business.phone}</span>
                    </div>
                )}
                {business.website && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Globe className="h-4 w-4" />
                        <a href={business.website} target="_blank" rel="noopener noreferrer" className="hover:underline text-primary">
                            {business.website}
                        </a>
                    </div>
                )}
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2 pt-2">
              {business.categories.map((category) => (
                <Badge key={category} variant="outline">
                  <Tag className="h-3 w-3 mr-1" />
                  {category}
                </Badge>
              ))}
            </div>

            {/* Google Places ID */}
            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Google Places ID: <span className="font-mono">{business.id}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center max-w-md mx-auto">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Back to Search
        </Button>
        <Button onClick={onConfirm} className="flex-1 gap-2">
          Yes, Train Sophie AI
          <Sparkles className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
